
import { MultiApp } from '@/config/context';
import { clearAllCookie, delCookie, getCookie, setCookie } from '@raft/utils';
import { BaseUser } from './base-user';
import { AuthUser, ProUserInfo } from './types';
import {
  BUGLY_LOGIN_STORAGE_KEY,
  genAvatar,
  genCookieName,
  getTdsPassportConfig,
  getTdsPassportRootDomain,
  TDS_SASS_TOKEN,
  TDS_SASS_UIN,
  TDS_TOKEN,
  TDS_USER_ID,
  TDS_USER_NAME,
} from './utils';

const defaultAuthInfo: AuthUser = {
  isLoggedIn: false,
};

/**
 * 不需要校验登录态的页面路由配置
 */
const noNeedCheckLoginList = [
  '/login',
  '/login_sgp',
  '/register',
  '/select-account',
  '/security',
  '/index/main',
  '/index/tds-main',
  '/index/rightly',
  '/',
  '/alert-notice',
  '/join',
];

const passportConfig = getTdsPassportConfig(MultiApp.Eu);
const passportRootDomain = getTdsPassportRootDomain(passportConfig);

const PASSPORT_LOGIN_EXPIRES_IN_DAY = 30;

const PASSPORT_LOGIN_PARAM_MAP = {
  token: 'tds_token',
  sassToken: 'tds_sassToken',
  sassUin: 'tds_sassUin',
  userId: 'tds_userId',
  userName: 'tds_userName',
};

const clearPassportLoginParamsFromUrl = () => {
  const url = new URL(window.location.href);
  Object.values(PASSPORT_LOGIN_PARAM_MAP).forEach(param => url.searchParams.delete(param));
  window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
};

const setPassportCookie = (name: string, value: string) => {
  if (!value) return;

  const cookieName = genCookieName(name);
  delCookie(cookieName, '/');
  delCookie(cookieName, '/', passportRootDomain);
  setCookie(cookieName, value, PASSPORT_LOGIN_EXPIRES_IN_DAY, '/', passportRootDomain);
};

const syncPassportLoginParamsToCookie = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get(PASSPORT_LOGIN_PARAM_MAP.token);

  if (!token) {
    return;
  }

  const sassToken = params.get(PASSPORT_LOGIN_PARAM_MAP.sassToken) || '';
  const sassUin = params.get(PASSPORT_LOGIN_PARAM_MAP.sassUin) || '';
  const userId = params.get(PASSPORT_LOGIN_PARAM_MAP.userId) || '';
  const userName = params.get(PASSPORT_LOGIN_PARAM_MAP.userName) || '';

  setPassportCookie(TDS_TOKEN, token);
  setPassportCookie(TDS_SASS_TOKEN, sassToken);
  setPassportCookie(TDS_SASS_UIN, sassUin);
  setPassportCookie(TDS_USER_ID, userId);
  setPassportCookie(TDS_USER_NAME, userName);

  if (userId || userName) {
    localStorage.setItem(BUGLY_LOGIN_STORAGE_KEY, JSON.stringify({
      buglyId: userId,
      buglyUserId: userId,
      name: userName,
      buglyUserName: userName,
    }));
  }

  clearPassportLoginParamsFromUrl();
};

/**
 * 删除登录链路种下的 cookie。
 * EU cookie 根域按环境区分：测试环境 tds.qq.com，正式环境 frontarc.ai。
 */
export function clearLoginCookies() {
  delCookie(genCookieName(TDS_TOKEN), '/', passportRootDomain);
  delCookie(genCookieName(TDS_SASS_TOKEN), '/', passportRootDomain);
  delCookie(genCookieName(TDS_SASS_UIN), '/', passportRootDomain);
  delCookie(genCookieName(TDS_USER_ID), '/', passportRootDomain);
  delCookie(genCookieName(TDS_USER_NAME), '/', passportRootDomain);
}

/**
 * 欧洲版用户实现。
 * 页面能力按 Pro 基线，登录链路复用当前域名下的 /login_sgp，由 nginx 转发到实际登录服务。
 */
export default class EuUser implements BaseUser<EuUser> {
  onLogin(): boolean {
    return true;
  }
  logout(): void {
    localStorage.removeItem(BUGLY_LOGIN_STORAGE_KEY);
    // 这个只能清除当前域名的
    clearAllCookie();
    // 需要单独针对登录根域下的 cookie 清除
    clearLoginCookies();
    const currentUrl = encodeURIComponent(location.href);
    const redirectURL = new URL(location.origin);
    redirectURL.pathname = passportConfig.path;
    redirectURL.searchParams.set('url', currentUrl);
    redirectURL.searchParams.set('app', passportConfig.appName);
    window.location.href = redirectURL.toString();
  }
  checkLogin(pathname: string): boolean {
    const authInfo = this.initUserInfo();
    const noNeedCheck = noNeedCheckLoginList.includes(pathname);
    // 检查登录态
    if (!noNeedCheck && !authInfo.isLoggedIn) {
      this.logout();
      return false;
    }

    return true;
  }
  // todo any
  initUserInfo(): AuthUser {
    try {
      syncPassportLoginParamsToCookie();
      const token = this.getBuglyToken();
      const saasToken = this.getSaasToken();
      return (token && saasToken) ? {
        isLoggedIn: true,
        token,
        saasToken,
        userInfo: this.getUserInfo() as ProUserInfo,
      } : defaultAuthInfo;
    } catch (e) {
      return defaultAuthInfo;
    }
  }
  getUserInfo(): EuUser | null {
    try {
      return JSON.parse(localStorage.getItem(BUGLY_LOGIN_STORAGE_KEY) as string) as EuUser;
    } catch (e) {
      return null;
    }
  }
  getAvatar(): string {
    const userName = this.getUserName();
    return genAvatar(userName);
  }

  public getUserName(): string {
    return decodeURIComponent(getCookie(genCookieName(TDS_USER_NAME)) || '');
  }

  public getUserId() {
    return getCookie(genCookieName(TDS_USER_ID)) || '';
  }

  public getSaasToken() {
    return getCookie(genCookieName(TDS_SASS_TOKEN)) || '';
  }

  public getBuglyToken() {
    return getCookie(genCookieName(TDS_TOKEN)) || '';
  }

  public getSassUin() {
    return getCookie(genCookieName(TDS_SASS_UIN)) || '';
  }
}
